/**
 * Act2TopBar — 第二幕顶部栏
 *
 * 显示轮次、话题阶段、双方指标条
 */
import React from 'react';
import { Settings, Heart, Shield, Zap } from 'lucide-react';
import type { CharacterMetrics } from '@shared/types/character';
import type { TopicStage } from '@shared/types/cards';
import { ACT2_MAX_ROUNDS } from '@shared/constants/gameBalance';

interface Act2TopBarProps {
  round: number;
  topicStage: TopicStage;
  metricsA: CharacterMetrics;
  metricsB: CharacterMetrics;
  nameA: string;
  nameB: string;
  scene: string;
  onSettingsClick?: () => void;
}

const TOPIC_LABELS: Record<TopicStage, { label: string; color: string }> = {
  '破冰': { label: '破冰', color: 'bg-blue-100 text-blue-700' },
  '兴趣': { label: '兴趣', color: 'bg-green-100 text-green-700' },
  '情感': { label: '情感', color: 'bg-pink-100 text-pink-700' },
  '价值观': { label: '价值观', color: 'bg-purple-100 text-purple-700' },
  '未来': { label: '未来', color: 'bg-amber-100 text-amber-700' },
};

export const Act2TopBar: React.FC<Act2TopBarProps> = React.memo(({
  round,
  topicStage,
  metricsA,
  metricsB,
  nameA,
  nameB,
  scene,
  onSettingsClick,
}) => {
  const topic = TOPIC_LABELS[topicStage];

  return (
    <div className="bg-white/80 backdrop-blur-md border-b border-[#E5E0D8] flex flex-col z-10 shrink-0">
      {/* Turn counter + topic + settings */}
      <div className="flex items-center justify-center pt-1.5 pb-0.5 relative gap-2">
        <span className="text-[10px] font-medium text-[#9CA3AF]">
          第二幕 · {scene} · 第 {round}/{ACT2_MAX_ROUNDS} 轮
        </span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${topic.color}`}>
          {topic.label}
        </span>
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#F5EDE3] transition-colors text-[#C4B9AC] hover:text-[#6B7280]"
            aria-label="设置"
          >
            <Settings size={14} />
          </button>
        )}
      </div>

      {/* Metrics bars */}
      <div className="px-3 pb-2 space-y-1">
        <MetricsRow name={nameA} metrics={metricsA} color="coral" />
        <MetricsRow name={nameB} metrics={metricsB} color="purple" />
      </div>
    </div>
  );
});

Act2TopBar.displayName = 'Act2TopBar';

// ── 单人指标行 ──────────────────────────────────

interface MetricsRowProps {
  name: string;
  metrics: CharacterMetrics;
  color: 'coral' | 'purple';
}

const MetricsRow: React.FC<MetricsRowProps> = ({ name, metrics, color }) => {
  const accent = color === 'coral' ? 'text-coral-500' : 'text-purple-500';

  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-[9px] ${accent} font-medium w-10 shrink-0 truncate`}>{name}</span>
      <MetricBar icon={<Heart size={8} />} value={metrics.affection} max={100} color="bg-red-400" />
      <MetricBar icon={<Shield size={8} />} value={metrics.comfort} max={100} color="bg-blue-400" />
      <MetricBar icon={<Zap size={8} />} value={metrics.energy} max={100} color="bg-amber-400" />
    </div>
  );
};

interface MetricBarProps {
  icon: React.ReactNode;
  value: number;
  max: number;
  color: string;
}

const MetricBar: React.FC<MetricBarProps> = ({ icon, value, max, color }) => {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="flex items-center gap-0.5 flex-1">
      <span className="text-[#9CA3AF]">{icon}</span>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[8px] text-[#9CA3AF] w-5 text-right">{value}</span>
    </div>
  );
};
